/*
 * Copyright 2026 North Carolina State University
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const useFormCache = (cacheKey, initialData = {}) => {
  const [cachedData, setCachedData] = useState(initialData);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef(null);
  const lastSaveTimeRef = useRef(null);

  // Load cached data on mount
  useEffect(() => {
    try {
      // Check if localStorage is available
      if (typeof Storage === "undefined") {
        console.warn('localStorage is not supported in this browser');
        setIsLoading(false);
        return;
      }

      const cached = localStorage.getItem(cacheKey);
      
      if (cached) {
        const parsedData = JSON.parse(cached);
        setCachedData(parsedData);
      }
    } catch (error) {
      console.error('❌ Error loading cached form data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [cacheKey]);

  // Debounced save function
  const saveToCache = useCallback((data) => {
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set saving indicator
    setIsSaving(true);

    // Debounce the save operation
    saveTimeoutRef.current = setTimeout(() => {
      try {
        // Check if localStorage is available
        if (typeof Storage === "undefined") {
          console.warn('localStorage is not supported in this browser');
          setIsSaving(false);
          return;
        }

        const serializedData = JSON.stringify(data);
        localStorage.setItem(cacheKey, serializedData);
        lastSaveTimeRef.current = new Date();
        
        // Verify the save worked
        const verification = localStorage.getItem(cacheKey);
        if (verification !== serializedData) {
          console.error('❌ Cache verification failed - data may not have been saved');
        }
      } catch (error) {
        console.error('❌ Error caching form data:', error);
        if (error.name === 'QuotaExceededError') {
          console.error('localStorage quota exceeded. Consider clearing old data.');
        }
      } finally {
        setIsSaving(false);
      }
    }, 500); // 500ms debounce
  }, [cacheKey]);

  // Update cached data and save to localStorage
  const updateCache = useCallback((newData) => {
    setCachedData(newData);
    saveToCache(newData);
  }, [saveToCache]);

  // Clear cache
  const clearCache = useCallback(() => {
    try {
      localStorage.removeItem(cacheKey);
      setCachedData(initialData);
    } catch (error) {
      console.error('Error clearing form cache:', error);
    }
  }, [cacheKey, initialData]);

  // Get last save time
  const getLastSaveTime = useCallback(() => {
    return lastSaveTimeRef.current;
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    cachedData,
    updateCache,
    clearCache,
    isLoading,
    isSaving,
    getLastSaveTime
  };
};

export default useFormCache;
